import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import styled from "styled-components/macro";

import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import { Title, sidePadding } from "Payments/PaymentPage/Shared/style.js";
import { useGetSelectedPaymentMethodDetails } from "Payments/PaymentPage/Shared/useGetSelectedPaymentMethodDetails";
import { getCardTypePretty } from "Payments/Shared/getCardTypePretty";
import qs from "qs";

import FnrButton, { ButtonText } from "UI/FnrButton3";
import FnrIcon from "UI/FnrIcon";
import FnrPaymentMethodIcon from "UI/FnrPaymentMethodIcon";
import {
  SharedResizingChild,
  SharedResizingContainer,
} from "UI/SharedResizingArea";

import { formatAmountInCentsForDisplay } from "Utilities/Number/formatAmountInCentsForDisplay";

import ErrorMessage from "../Shared/ErrorMessage";
import { ScreenWrapper } from "../Shared/ScreenWrapper";
import { useGetPaymentTypeVerbiage } from "../Shared/useGetPaymentTypeVerbiage";

import CancelPaymentButton from "./CancelPaymentButton";
import FinalizePaymentButton from "./FinalizePaymentButton";
import PaymentCancelled from "./PaymentCancelled";

export default function ModifyOrCancelScreen() {
  const { state, dispatch } = usePaymentPage();
  const { noun } = useGetPaymentTypeVerbiage();
  const { originalAmount } = state.modifyPayment;
  const { fundColorSafe, description: fundName } = state.fund;
  const [isLoading, setIsLoading] = useState(null); // "cancel", "finalize"
  const [errorMsg, setErrorMsg] = useState(null);
  const [cancellationComplete, setCancellationComplete] = useState(false);
  const { cardType, nickname, last4 } = useGetSelectedPaymentMethodDetails();
  const cardTypePretty = getCardTypePretty(cardType);
  const paymentAlreadyCanceled = state.modifyPayment?.paymentAlreadyCanceled;

  const containerRef = useRef();

  const location = useLocation();
  const { keyword } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  const handleModifyClick = () => {
    // the "amountToSet" should be in dollars (not cents), and not include ".00" if the original amount
    // was an even dollar amount (this improves the experience of updating the amount, especially on mobile)
    const amountInDollars = Number(originalAmount / 100).toFixed(2);
    const amountToSet = String(amountInDollars).endsWith(".00")
      ? Number(amountInDollars).toFixed(0)
      : amountInDollars;

    dispatch({
      type: "setAmount",
      payload: amountToSet,
    });
    dispatch({
      type: "setScreenControlFlag",
      flag: "isModifyingScheduledPayment",
      value: "true",
    });
  };

  return (
    <ScreenWrapper ref={containerRef}>
      <SharedResizingContainer bounce forceWidthWrapper={containerRef}>
        {cancellationComplete || paymentAlreadyCanceled ? (
          <SharedResizingChild key="cancelled">
            <PaymentCancelled
              noun={noun}
              fundColor={fundColorSafe}
              canceledPreviously={paymentAlreadyCanceled}
            />
          </SharedResizingChild>
        ) : (
          <SharedResizingChild key="modify">
            <ModifyWrapper>
              <TitleText>
                Here&apos;s your recent transaction
                {keyword ? (
                  <span>
                    {" "}
                    from texting{" "}
                    <span style={{ fontStyle: "italic" }}>{keyword}</span>.
                  </span>
                ) : (
                  ":"
                )}
              </TitleText>

              <Row>
                <FundIcon>
                  <FnrIcon fas fa-piggy-bank />
                </FundIcon>
                <FundName>{fundName}</FundName>
              </Row>
              <Row>
                <AmountIcon>
                  <FnrIcon fas fa-dollar-sign />
                </AmountIcon>
                <Amount>
                  ${formatAmountInCentsForDisplay(originalAmount)}
                </Amount>
              </Row>
              <Row>
                <Icon>
                  <FnrPaymentMethodIcon cardType={cardType} />
                </Icon>
                <Card>
                  {nickname || cardTypePretty}
                  <CardNumber>
                    <Dots>••••</Dots>
                    {last4}
                  </CardNumber>
                </Card>
              </Row>

              <ErrorMessage message={errorMsg || ""} />

              <FinalizePaymentButton
                setErrorMsg={setErrorMsg}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
              />

              <ButtonWrapper>
                <CancelPaymentButton
                  setErrorMsg={setErrorMsg}
                  setCancellationComplete={setCancellationComplete}
                  setIsLoading={setIsLoading}
                  isLoading={isLoading}
                />

                <FnrButton
                  onClick={handleModifyClick}
                  fullWidth
                  disabled={isLoading === "cancel" || isLoading === "finalize"}
                >
                  <ButtonText>Modify</ButtonText>
                </FnrButton>
              </ButtonWrapper>
            </ModifyWrapper>
          </SharedResizingChild>
        )}
      </SharedResizingContainer>
    </ScreenWrapper>
  );
}

const ModifyWrapper = styled.div`
  padding: 28px;
  ${sidePadding}
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 14px;
  padding-top: 28px;
`;

const TitleText = styled(Title)`
  margin-bottom: 28px;
`;

const FundName = styled.div``;
const Amount = styled.div``;
export const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
`;

export const CardNumber = styled.div`
  display: flex;
  align-items: center;
  color: #5b5b5b;
  font-size: 16px;
`;

export const Dots = styled.div`
  font-size: 21px;
  line-height: 21px;
  margin-right: 4px;
  margin-left: 12px;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
  font-size: 20px;
`;

export const Icon = styled.div`
  width: 40px;
  height: 40px;
  flex-grow: 0;
  flex-shrink: 0;
  background: white;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  color: #fff;
  text-shadow: rgba(0, 0, 0, 0.2) 1px 1px 2px;
  position: relative;
  overflow: hidden;
`;
const FundIcon = styled(Icon)`
  background-color: var(--fundColor);
`;
const AmountIcon = styled(Icon)`
  background: #00b44e;
`;
